// src/components/creditnote/InvoiceReport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { decryptData } from "../../utils/encrypt";
import {
  INVOICE_REPORT_SEARCH_API,
  INVOICE_ACTUAL_RECEIPT_SAVE_API,
} from "../../config/api";

const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtDMY = (v, { forceFirstOfMonth = false } = {}) => {
  // pick a base date: provided value or today
  let dt = v ? new Date(String(v).slice(0, 10)) : new Date();

  // if requested, snap to the 1st of the same month/year
  if (forceFirstOfMonth) dt = new Date(dt.getFullYear(), dt.getMonth(), 1);

  const y = dt.getFullYear();
  const m = dt.getMonth() + 1;
  const d = dt.getDate();
  const yy = String(y).slice(-2);

  return `${String(d).padStart(2, "0")}-${MON[m - 1]}-${yy}`;
};

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const defaultDates = () => {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth(), 1); // 1st of this month
  return { from: toYMD(from), to: toYMD(to) };
};

const n3 = (x) => Number(x ?? 0).toFixed(3);

export default function InvoiceReport() {
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);

  const { from, to } = defaultDates();
  const [filters, setFilters] = useState({
    invoice_no: "",
    customer: "",
    date_from: from,
    date_to: to,
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

const numeric = new Set([
  "invoice_qty_total",
  "actual_qty_total",
  "actual_last_qty",
  "credited_qty_total",
  "actual_last_credit_note_qty",   // << add
  "credit_note_qty",
]);


  const [sort, setSort] = useState({ by: "invoice_date", dir: "desc" });
  const sIcon = (col) => (sort.by === col ? (sort.dir === "asc" ? "▲" : "▼") : "");
  const onSort = (col) =>
    setSort((s) =>
      s.by === col ? { by: col, dir: s.dir === "asc" ? "desc" : "asc" } : { by: col, dir: "asc" }
    );

  useEffect(() => {
    try {
      const su = localStorage.getItem("suyog_user");
      if (su) {
        const u = decryptData(su);
        const id = u?.user_id || null;
        setUserId((prev) => (prev === id ? prev : id));
      }
    } catch {}
    try {
      const so = localStorage.getItem("suyog_selected_org");
      if (so) {
        const o = JSON.parse(so);
        const id = o?.value || null;
        setOrgId((prev) => (prev === id ? prev : id));
      }
    } catch {}
  }, []);

  // one request at a time
  const didInitialFetch = useRef(false);
  const inFlightRef     = useRef(false);
  const abortRef        = useRef(null);

  const fetchRows = async (override, { replaceInFlight = false } = {}) => {
    if (!userId || !orgId) return;
    if (inFlightRef.current) {
      if (!replaceInFlight) return;
      try { abortRef.current?.abort(); } catch {}
    }

    const controller = new AbortController();
    abortRef.current = controller;
    inFlightRef.current = true;

    setLoading(true);
    setError("");
    try {
      const f = override ?? filters;
      const payload = {
        user_id: userId,
        org_id:  orgId,
        date_from: f.date_from,
        date_to:   f.date_to,
        invoice_no: f.invoice_no || undefined,
        customer:   f.customer   || undefined,
      };
      const res = await fetch(INVOICE_REPORT_SEARCH_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const json = await res.json();
      if (!json?.success) throw new Error(json?.error || "Failed to load");
      setRows(Array.isArray(json.rows) ? json.rows : []);
    } catch (e) {
      if (e?.name !== "AbortError") {
        setRows([]);
        setError(e?.message || "Server error");
      }
    } finally {
      if (abortRef.current === controller) {
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!userId || !orgId) return;
    if (!didInitialFetch.current) {
      didInitialFetch.current = true;
      fetchRows();
    }
  }, [userId, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const onSearch = (e) => {
    e?.preventDefault?.();
    fetchRows(undefined, { replaceInFlight: false });
  };

  const onReset = () => {
    const d = defaultDates();
    const reset = { invoice_no: "", customer: "", date_from: d.from, date_to: d.to };
    setFilters(reset);
    fetchRows(reset, { replaceInFlight: true });
  };

  const sortedRows = useMemo(() => {
    const data = rows.slice();
    const { by, dir } = sort;
    data.sort((a, b) => {
      const va = a?.[by];
      const vb = b?.[by];
      if (numeric.has(by)) {
        const na = Number(va ?? 0);
        const nb = Number(vb ?? 0);
        return dir === "asc" ? na - nb : nb - na;
      }
      const sa = (va ?? "").toString();
      const sb = (vb ?? "").toString();
      const cmp = sa.localeCompare(sb);
      return dir === "asc" ? cmp : -cmp;
    });
    return data;
  }, [rows, sort]);

  const totals = useMemo(() => {
    let invQty = 0, actQty = 0, cnQty = 0;
    for (const r of rows) {
      invQty += Number(r.invoice_qty_total || 0);
      actQty += Number(r.actual_qty_total || 0);
      // only count credit note qty if last record is marked for CN
      const countCN = Number(r.actual_last_count_cn ?? 0) === 1;
      cnQty += countCN ? Number(r.credit_note_qty || 0) : 0;
    }
    return { invQty, actQty, cnQty };
  }, [rows]);



  const renderCreditNotes = (r) => {
    if (!r?.credit_note_nos) return "-";
    const list = String(r.credit_note_nos).split(",").map(s => s.trim()).filter(Boolean);
    if (!list.length) return "-";
    return (
      <div className="d-flex flex-wrap gap-1">
        {list.map((no, i) => (
          <span key={i} className="badge text-bg-light border" title={no}>{no}</span>
        ))}
      </div>
    );
  };

  // modal + save (server handles arithmetic)
  const [showModal, setShowModal] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [form, setForm] = useState({ qty: "", remark: "", countForCreditNote: true });
  const [saving, setSaving] = useState(false);

  const openModal = (row) => {
    setActiveRow(row);
    setForm({
      qty: row?.actual_last_qty != null ? String(row.actual_last_qty) : "",
      remark: row?.actual_last_remark ?? "",
      countForCreditNote: row?.actual_last_count_cn != null ? !!Number(row.actual_last_count_cn) : true,
    });
    setShowModal(true);
  };
  const closeModal = () => { if (!saving) { setShowModal(false); setActiveRow(null); } };
  const onFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? !!checked : value }));
  };

  const saveActualReceipt = async () => {
    if (!activeRow) return;
    const qty = Number(form.qty);
    const isCountCN = !!form.countForCreditNote;

    if (!Number.isFinite(qty) || qty < 0) {
      alert("Please enter a valid Actual Delivery Qty (>= 0).");
      return;
    }
    if (isCountCN && qty <= 0) {
      alert("When 'Count for Credit note' is checked, quantity must be > 0.");
      return;
    }

    const payload = {
      user_id: userId,
      org_id:  orgId,
      tally_invoice_no: activeRow.tally_invoice_no,
      actual_qty: qty,
      remark: (form.remark ?? "").trim(),
      count_for_credit_note: isCountCN ? 1 : 0,
    };

    try {
      setSaving(true);
      const res = await fetch(INVOICE_ACTUAL_RECEIPT_SAVE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!json?.success) throw new Error(json?.error || "Save failed");

      setRows(prev =>
        prev.map(r =>
          r.tally_invoice_no === activeRow.tally_invoice_no
            ? {
                ...r,
                credited_qty_total: Number(json.totals?.credited_qty_total ?? r.credited_qty_total ?? 0),
                actual_qty_total:   Number(json.totals?.actual_qty_total   ?? r.actual_qty_total   ?? 0),
                credit_note_qty:    Number(json.totals?.credit_note_qty    ?? json.last?.credit_note_qty ?? r.credit_note_qty ?? 0),
                actual_last_id:     json.last?.id ?? r.actual_last_id ?? null,
                actual_last_qty:    json.last?.actual_qty ?? r.actual_last_qty ?? null,
                actual_last_remark: json.last?.remark ?? r.actual_last_remark ?? null,
                actual_last_count_cn: json.last?.count_for_credit_note ?? r.actual_last_count_cn ?? null,
                actual_last_on:     json.last?.recorded_on ?? r.actual_last_on ?? null,
              }
            : r
        )
      );
      closeModal();
    } catch (e) {
      alert(e?.message || "Failed to save actual receipt");
    } finally {
      setSaving(false);
    }
  };


  // ---- LP parsing helper (pairs string -> Map(name -> qty)) ----
const parseLPPairs = (pairs) => {
  const map = new Map();
  if (!pairs) return map;
  // pairs format: "LP A:12.500,LP B:7.000"
  for (const token of String(pairs).split(",")) {
    const idx = token.lastIndexOf(":");
    if (idx === -1) continue;
    const name = token.slice(0, idx).trim();
    const qty  = Number(token.slice(idx + 1)) || 0;
    map.set(name, (map.get(name) || 0) + qty);
  }
  return map;
};



  // ---- CSV Export (filtered/sorted rows) ----
// ---- CSV Export (filtered/sorted rows, with dynamic Loading Point columns) ----
// ---- CSV Export (filtered/sorted rows, with dynamic Loading Point columns) ----
const csvEscape = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val).replace(/\r?\n|\r/g, " ");
  return (s.includes('"') || s.includes(",")) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Build CSV string for the given rows
const __buildInvoiceCsv = (data) => {
  // collect all LP names present in this filtered set
  const lpNamesSet = new Set();
  const lpMaps = data.map((r) => {
    const m = parseLPPairs(r.lp_pairs);
    for (const k of m.keys()) lpNamesSet.add(k);
    return m;
  });
  const lpNames = Array.from(lpNamesSet).sort((a, b) => a.localeCompare(b));

  // base columns to export
  const baseHeaders = [
    "Invoice No",
    "Invoice Date",
    "Customer",
    "Invoiced Qty",
    "Actual Unload Qty",
    "Credit note Qty",
    "CN No",
    "Count CN?",
    "Material",
    "Packing Type",
    "Transporter",
    "Vehicle No",
    "Destination City",
  ];

  // final headers = base + all LP columns
  const headers = [...baseHeaders, ...lpNames.map((n) => `LP: ${n}`)];

  const rowsCsv = data.map((r, idx) => {
    const isCountCNLast = Number(r.actual_last_count_cn ?? 0) === 1;
    const cnShown = isCountCNLast ? Number(r.credit_note_qty ?? 0) : 0;

    // Map for LPs of this row (kept in the same order as headers)
    const lpMap = lpMaps[idx];

    const base = [
      r.tally_invoice_no ?? "",
      fmtDMY(r.invoice_date),
      r.customer_name ?? "",
      n3(r.invoice_qty_total ?? 0),
      n3(r.actual_qty_total ?? 0),
      n3(cnShown),
      r.actual_last_remark ?? "",
      isCountCNLast ? "Yes" : "No",
      r.material_names ?? "",
      r.packing_types ?? "",
      r.transporter_names ?? "",
      r.vehicle_nos ?? "",
      r.destination_cities ?? "",
    ];

    const lpCells = lpNames.map((name) => n3(lpMap.get(name) || 0));

    return [...base, ...lpCells].map(csvEscape).join(",");
  });

  return [headers.map(csvEscape).join(","), ...rowsCsv].join("\n");
};

const onExportCsv = () => {
  const csv = __buildInvoiceCsv(sortedRows); // export what's visible (sorted & filtered)
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice_report_${filters.date_from}_to_${filters.date_to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};


  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h4>📑 Invoice Report</h4>

        <form className="card mt-3" onSubmit={onSearch}>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Invoice No</label>
                <input className="form-control" name="invoice_no" value={filters.invoice_no} onChange={onChange} placeholder="e.g. T-4846/25-26" />
              </div>
              <div className="col-md-3">
                <label className="form-label">Customer</label>
                <input className="form-control" name="customer" value={filters.customer} onChange={onChange} placeholder="Account name contains…" />
              </div>
              <div className="col-md-3">
                <label className="form-label">Date From</label>
                <input type="date" className="form-control" name="date_from" value={filters.date_from} onChange={onChange} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Date To</label>
                <input type="date" className="form-control" name="date_to" value={filters.date_to} onChange={onChange} />
              </div>
            </div>

            <div className="mt-3 d-flex gap-2">
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? "Loading..." : "Search"}
              </button>
              <button className="btn btn-secondary" type="button" onClick={onReset} disabled={loading}>
                Reset
              </button>

          <button
  className="btn btn-success"
  type="button"
  onClick={onExportCsv}
  disabled={loading || sortedRows.length === 0}
>
  Export CSV
</button>


            </div>

            {error && <div className="text-danger mt-2">{error}</div>}
          </div>
        </form>

        <div className="table-responsive mt-4">
          <table className="table table-bordered table-hover w-100">
            <thead className="table-light">
              <tr>
                <th onClick={() => onSort("tally_invoice_no")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>Invoice No {sIcon("tally_invoice_no")}</th>
                <th onClick={() => onSort("invoice_date")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>Invoice Date {sIcon("invoice_date")}</th>
                <th onClick={() => onSort("customer_name")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>Customer {sIcon("customer_name")}</th>
                <th className="text-end" onClick={() => onSort("invoice_qty_total")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>Invoiced Qty {sIcon("invoice_qty_total")}</th>
                <th className="text-end" onClick={() => onSort("actual_qty_total")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>Actual Unload Qty {sIcon("actual_qty_total")}</th>
                <th className="text-end" onClick={() => onSort("credit_note_qty")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>Credit note Qty {sIcon("credit_note_qty")}</th>
                <th style={{ whiteSpace: "nowrap" }}>CN No</th>
                <th style={{ whiteSpace: "nowrap" }}>Count CN?</th>
                <th style={{ whiteSpace: "nowrap" }}>Actual Receipt</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
  <tr><td colSpan="10" className="text-center">No data</td></tr>
) : (
  sortedRows.map((r) => {
    const inv  = Number(r.invoice_qty_total ?? 0);
    const act  = Number(r.actual_qty_total  ?? 0);
    const last = Number(r.actual_last_qty   ?? 0);

    // show credit_note_qty only when last record is marked for CN
    const isCountCNLast = Number(r.actual_last_count_cn ?? 0) === 1;
    const cn = isCountCNLast ? Number(r.credit_note_qty ?? 0) : 0;

    // highlight only if we are actually showing a non-zero CN qty
    const highlight = cn !== 0;

    return (
      <tr key={r.tally_invoice_no} className={highlight ? "table-warning" : ""}>
        <td>{r.tally_invoice_no}</td>
        <td>{fmtDMY(r.invoice_date)}</td>
        <td>{r.customer_name || "-"}</td>
        <td className="text-end">{n3(inv)}</td>
        <td className="text-end">{n3(act)}</td>
        <td className="text-end fw-bold">{n3(cn)}</td>
        <td style={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {r.actual_last_remark || "-"}
        </td>
        <td>{isCountCNLast ? "Yes" : "No"}</td>
        <td>
          <div className="mb-2">{renderCreditNotes(r)}</div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => openModal(r)}
          >
            Record Actual
          </button>
          {r.actual_last_on && (
            <div className="small text-muted mt-1">
              Last: {n3(r.actual_last_qty ?? 0)} on {fmtDMY(r.actual_last_on)}
            </div>
          )}
        </td>
      </tr>
    );
  })
)}

            </tbody>
            <tfoot className="table-light">
              <tr>
                <td colSpan={3} className="text-end fw-bold">Totals:</td>
                <td className="text-end fw-bold">{n3(totals.invQty)}</td>
                <td className="text-end fw-bold">{n3(totals.actQty)}</td>
                <td className="text-end fw-bold">—</td>
                <td className="text-end fw-bold">{n3(totals.cnQty)}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }} tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Record / Update Actual Receipt</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeModal} disabled={saving}/>
              </div>
              <div className="modal-body">
                <div className="mb-2 small text-muted">
                  <div><strong>Invoice:</strong> {activeRow?.tally_invoice_no}</div>
                  <div><strong>Customer:</strong> {activeRow?.customer_name || "-"}</div>
                  <div><strong>Invoice Date:</strong> {fmtDMY(activeRow?.invoice_date)}</div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Actual Delivery Qty</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    className="form-control"
                    name="qty"
                    value={form.qty}
                    onChange={onFormChange}
                    placeholder="e.g. 12.500"
                  />
                  <div className="form-text">If “Count for Credit note?” is unchecked, you may enter 0.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Remark</label>
                  <textarea className="form-control" rows={3} name="remark" value={form.remark} onChange={onFormChange} placeholder="Optional" />
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="countForCreditNote" name="countForCreditNote" checked={form.countForCreditNote} onChange={onFormChange} />
                  <label className="form-check-label" htmlFor="countForCreditNote">Count for Credit note?</label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" type="button" onClick={closeModal} disabled={saving}>Cancel</button>
                <button className="btn btn-primary" type="button" onClick={saveActualReceipt} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showModal && <div className="modal-backdrop fade show" />}

      <Footer />
    </>
  );
}
